import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Filter, Pencil, ArrowLeft } from 'lucide-react';
import DualScrollTable from '../../../components/common/DualScrollTable';
import { useNavigate } from 'react-router-dom';
import { supabaseLeaseholderService } from '../../../services/supabase/leaseholders';
import AddEditLeaseholderModal from './components/AddEditLeaseholderModal';
import type { Leaseholder } from '../../../types';

export default function LeaseholdersAdmin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLeaseholder, setSelectedLeaseholder] = useState<Leaseholder | null>(null);
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);

  const { data: leaseholders = [], isLoading } = useQuery({
    queryKey: ['admin', 'leaseholders'],
    queryFn: supabaseLeaseholderService.getAll
  });

  const filteredLeaseholders = leaseholders.filter(holder => 
    holder.holder_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    holder.holder_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    holder.holder_abn?.toString().includes(searchQuery)
  );

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'leaseholders'] });
  };

  const handleEdit = (leaseholder: Leaseholder) => {
    setSelectedLeaseholder(leaseholder);
    setIsAddEditModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="flex-1 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Manage Leaseholders</h1>
          <p className="mt-1 text-sm text-gray-500">
            Add, edit, and manage leaseholder information
          </p>
        </div>
        <button 
          onClick={() => {
            setSelectedLeaseholder(null);
            setIsAddEditModalOpen(true);
          }}
          className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Leaseholder
        </button>
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search leaseholders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <DualScrollTable>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Edit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                  Business Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ABN
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Position
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredLeaseholders.map((holder) => (
                <tr key={holder.holder_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(holder)}
                      className="text-primary hover:text-primary-dark"
                    >
                      <Pencil className="h-5 w-5" />
                    </button>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    <div className="truncate w-40" title={holder.holder_name}>
                      {holder.holder_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {holder.holder_contact || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {holder.holder_email || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {holder.holder_phone || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {holder.holder_abn?.toString() || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {holder.holder_position || 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DualScrollTable>
      </div>

      {isAddEditModalOpen && (
        <AddEditLeaseholderModal
          leaseholder={selectedLeaseholder}
          onClose={() => {
            setSelectedLeaseholder(null);
            setIsAddEditModalOpen(false);
          }}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
}
